import type { AxiosInstance } from 'axios'

import type {
  createEmbedPlaylistCollectionFeed,
  createEmbedUserTimelineHashtagFeed,
  createShowroomScheduleFeed,
  getChannelById,
  getMoreFeedItems,
} from './services/feed'
import {
  createEmbedPlaylistFeed,
  createEmbedUserProductSKUFeed,
  createEmbedUserTimelineFeed,
  getChannel,
} from './services/feed'
import type {
  getLiveStreamEvents,
  getLiveStreamMessages,
  getLiveStreamMessagesUntil,
  getLiveStreamPrivateMessages,
  getLiveStreamPrivateMessagesUntil,
  getLiveStreamStateSnapshot,
} from './services/livestream'
import type {
  getMicrositeByChannel,
  getMicrositeByChannelId,
  getSitemapPagesByChannelId,
  getSitemapVideosByChannel,
} from './services/microsites'
import type {
  checkVideoAccessCode,
  createTriviaGiveawayResult,
  createVideoInteractionResult,
  getConfigurationsByEmbedId,
  getHashtag,
  getPlaylistFromCohorts,
  getRemindMe,
  getVideo,
  getVideoEmailEmbed,
} from './services/uncategorized'
import { setupRedaxiosClient } from './setupRedaxiosClient'

type Tail<T extends unknown[]> = T extends [unknown, ...infer R] ? R : never

type TailTail<T extends unknown[]> = T extends [unknown, unknown, ...infer R]
  ? R
  : never

export type ApiService = ReturnType<typeof createApiService>

/**
 * Creates an API service with the given API host and context. Many API functions
 * are async loaded to reduce bundle size.
 */
export const createApiService = (
  params: {
    apiHost: string
    apiCache?: boolean
    apiCacheHost?: string
    apiCacheNonce?: string
  },
  __setupFunc = setupRedaxiosClient,
) => {
  const { apiHost, apiCache, apiCacheHost } = params
  const apiCacheNonce = params.apiCacheNonce ?? '1'

  const api: AxiosInstance = __setupFunc(apiHost)
  const apiForCache: AxiosInstance | undefined =
    apiCache && apiCacheHost ? __setupFunc(apiCacheHost) : undefined

  return {
    // Feed (essential, sync loaded)

    getChannel(...args: TailTail<Parameters<typeof getChannel>>) {
      return apiForCache
        ? getChannel(apiForCache, apiCacheNonce, ...args)
        : getChannel(api, apiCacheNonce, ...args)
    },
    createEmbedUserTimelineFeed(
      ...args: TailTail<Parameters<typeof createEmbedUserTimelineFeed>>
    ) {
      return apiForCache
        ? createEmbedUserTimelineFeed(apiForCache, apiCacheNonce, ...args)
        : createEmbedUserTimelineFeed(api, apiCacheNonce, ...args)
    },
    createEmbedPlaylistFeed(
      ...args: TailTail<Parameters<typeof createEmbedPlaylistFeed>>
    ) {
      return apiForCache
        ? createEmbedPlaylistFeed(apiForCache, apiCacheNonce, ...args)
        : createEmbedPlaylistFeed(api, apiCacheNonce, ...args)
    },
    createEmbedUserProductSKUFeed(
      ...args: TailTail<Parameters<typeof createEmbedUserProductSKUFeed>>
    ) {
      return apiForCache
        ? createEmbedUserProductSKUFeed(apiForCache, apiCacheNonce, ...args)
        : createEmbedUserProductSKUFeed(api, apiCacheNonce, ...args)
    },

    // Feed (non-essential, async loaded)

    async getChannelById(...args: Tail<Parameters<typeof getChannelById>>) {
      const m = await import('./services/feed')
      return await m.getChannelById(api, ...args)
    },
    async createEmbedUserTimelineHashtagFeed(
      ...args: TailTail<Parameters<typeof createEmbedUserTimelineHashtagFeed>>
    ) {
      const m = await import('./services/feed')
      return apiForCache
        ? await m.createEmbedUserTimelineHashtagFeed(
            apiForCache,
            apiCacheNonce,
            ...args,
          )
        : await m.createEmbedUserTimelineHashtagFeed(
            api,
            apiCacheNonce,
            ...args,
          )
    },
    async createEmbedPlaylistCollectionFeed(
      ...args: Tail<Parameters<typeof createEmbedPlaylistCollectionFeed>>
    ) {
      const m = await import('./services/feed')
      return await m.createEmbedPlaylistCollectionFeed(api, ...args)
    },
    async createShowroomScheduleFeed(
      ...args: TailTail<Parameters<typeof createShowroomScheduleFeed>>
    ) {
      const m = await import('./services/feed')
      return apiForCache
        ? await m.createShowroomScheduleFeed(
            apiForCache,
            apiCacheNonce,
            ...args,
          )
        : await m.createShowroomScheduleFeed(api, apiCacheNonce, ...args)
    },
    async getMoreFeedItems(...args: Tail<Parameters<typeof getMoreFeedItems>>) {
      const m = await import('./services/feed')
      return await m.getMoreFeedItems(api, ...args)
    },

    // Livestream

    async getLiveStreamMessages(
      ...args: Tail<Parameters<typeof getLiveStreamMessages>>
    ) {
      const m = await import('./services/livestream')
      return await m.getLiveStreamMessages(api, ...args)
    },
    async getLiveStreamMessagesUntil(
      ...args: Tail<Parameters<typeof getLiveStreamMessagesUntil>>
    ) {
      const m = await import('./services/livestream')
      return await m.getLiveStreamMessagesUntil(api, ...args)
    },
    async getLiveStreamPrivateMessages(
      ...args: Tail<Parameters<typeof getLiveStreamPrivateMessages>>
    ) {
      const m = await import('./services/livestream')
      return await m.getLiveStreamPrivateMessages(api, ...args)
    },
    async getLiveStreamPrivateMessagesUntil(
      ...args: Tail<Parameters<typeof getLiveStreamPrivateMessagesUntil>>
    ) {
      const m = await import('./services/livestream')
      return await m.getLiveStreamPrivateMessagesUntil(api, ...args)
    },
    async getLiveStreamEvents(
      ...args: Tail<Parameters<typeof getLiveStreamEvents>>
    ) {
      const m = await import('./services/livestream')
      return await m.getLiveStreamEvents(api, ...args)
    },
    async getLiveStreamStateSnapshot(
      ...args: Tail<Parameters<typeof getLiveStreamStateSnapshot>>
    ) {
      const m = await import('./services/livestream')
      return await m.getLiveStreamStateSnapshot(api, ...args)
    },

    // Microsites

    async getMicrositeByChannel(
      ...args: Tail<Parameters<typeof getMicrositeByChannel>>
    ) {
      const m = await import('./services/microsites')
      return await m.getMicrositeByChannel(api, ...args)
    },
    async getMicrositeByChannelId(
      ...args: Tail<Parameters<typeof getMicrositeByChannelId>>
    ) {
      const m = await import('./services/microsites')
      return await m.getMicrositeByChannelId(api, ...args)
    },
    async getSitemapVideosByChannel(
      ...args: Tail<Parameters<typeof getSitemapVideosByChannel>>
    ) {
      const m = await import('./services/microsites')
      return await m.getSitemapVideosByChannel(api, ...args)
    },
    async getSitemapPagesByChannelId(
      ...args: Tail<Parameters<typeof getSitemapPagesByChannelId>>
    ) {
      const m = await import('./services/microsites')
      return await m.getSitemapPagesByChannelId(api, ...args)
    },

    // Uncategorized

    async getVideo(...args: Tail<Parameters<typeof getVideo>>) {
      const m = await import('./services/uncategorized')
      return await m.getVideo(api, ...args)
    },
    async getHashtag(...args: Tail<Parameters<typeof getHashtag>>) {
      const m = await import('./services/uncategorized')
      return await m.getHashtag(api, ...args)
    },
    async createVideoInteractionResult(
      ...args: Tail<Parameters<typeof createVideoInteractionResult>>
    ) {
      const m = await import('./services/uncategorized')
      return await m.createVideoInteractionResult(api, ...args)
    },
    async createTriviaGiveawayResult(
      ...args: Tail<Parameters<typeof createTriviaGiveawayResult>>
    ) {
      const m = await import('./services/uncategorized')
      return await m.createTriviaGiveawayResult(api, ...args)
    },
    async getRemindMe(...args: Tail<Parameters<typeof getRemindMe>>) {
      const m = await import('./services/uncategorized')
      return await m.getRemindMe(api, ...args)
    },
    async getVideoEmailEmbed(
      ...args: Tail<Parameters<typeof getVideoEmailEmbed>>
    ) {
      const m = await import('./services/uncategorized')
      return await m.getVideoEmailEmbed(api, ...args)
    },
    async checkVideoAccessCode(
      ...args: Tail<Parameters<typeof checkVideoAccessCode>>
    ) {
      const m = await import('./services/uncategorized')
      return await m.checkVideoAccessCode(api, ...args)
    },
    async getPlaylistFromCohorts(
      ...args: Tail<Parameters<typeof getPlaylistFromCohorts>>
    ) {
      const m = await import('./services/uncategorized')
      return await m.getPlaylistFromCohorts(api, ...args)
    },
    async getConfigurationsByEmbedId(
      ...args: Tail<Parameters<typeof getConfigurationsByEmbedId>>
    ) {
      const m = await import('./services/uncategorized')
      return await m.getConfigurationsByEmbedId(api, ...args)
    },
  }
}
